import React, {
    useCallback,
    useEffect,
    useEffectEvent,
    useMemo,
    useRef,
    useState,
} from "react";
import { z } from "zod";
import { View, ViewSchema } from "@common/types/views/views";
import {
    useWorkspaceUserCache,
    WORKSPACE_USER_CACHE_KEYS,
} from "@hooks/useWorkspaceUserCache";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { mergeCachedView } from "@lib/mergeCachedView";
import {
    enforceLedgerColumns,
    offerScheduleColumnOnce,
} from "./accrualMatchView";
import { AccrualMatchColumn } from "./types";
import { AccrualMatchViewCtx } from "./accrualMatchView.context";
import { useAccrualMatchViewDefaults } from "./useAccrualMatchViewDefaults";

interface Props {
    children: React.ReactNode;
    /**
     * `"workspaceMatched"` (PRO-12048): default column set tailored to the
     * workspace's classification (category-first vs GL-first) and the
     * source transaction's ledger basis (cash-only vs dual-ledger) — see
     * `getDefaultAccrualMatchView`. Used by the primary transaction match
     * drawer (`TransactionMatchDrawer`/`AccrualMatchProvider`).
     *
     * `"legacy"` (default): the original single basis-swapped Account column.
     * Used by the reverse-match drawer (invoice/bill → transaction), which
     * this redesign's acceptance criteria don't cover and which lacks the
     * real `ledgerBasis`/`isGlFirst` this provider would otherwise need
     * (it manages selection outside `AccrualMatchProvider`, so `useAccrualMatch`
     * only returns context defaults there).
     */
    columnsVariant?: "workspaceMatched" | "legacy";
}

/**
 * Provides only the table-view context (column visibility/sizing/sort)
 * shared across the source / selected / search AccrualMatchTable
 * instances inside an accrual-match drawer.
 *
 * Use this when the drawer doesn't need the wider AccrualMatchCtx
 * (sourceTransaction / selectedItems / addItem / removeItem) — e.g. the
 * reverse-match drawer manages selection state externally.
 */
export const AccrualMatchViewProvider: React.FC<Props> = ({
    children,
    columnsVariant = "legacy",
}) => {
    // Separate cache keys per variant (PRO-12048): the two drawers' default
    // column sets diverged, so a view cached under one shape must not leak
    // into the other's ViewTable.
    const [cachedView, setCachedView] = useWorkspaceUserCache<View>(
        columnsVariant === "workspaceMatched"
            ? WORKSPACE_USER_CACHE_KEYS.ACCRUAL_MATCH_DRAWER_WORKSPACE_VIEW_CACHE_KEY
            : WORKSPACE_USER_CACHE_KEYS.ACCRUAL_MATCH_DRAWER_VIEW_CACHE_KEY,
        ViewSchema,
    );
    const { defaultView, viewConfig } =
        useAccrualMatchViewDefaults(columnsVariant);
    const { schedules: isSchedulesEnabled } = useTypedFlags();
    const [wasScheduleColumnOffered, setScheduleColumnOffered] =
        useWorkspaceUserCache<boolean>(
            columnsVariant === "workspaceMatched"
                ? WORKSPACE_USER_CACHE_KEYS.ACCRUAL_MATCH_DRAWER_WORKSPACE_SCHEDULE_COLUMN_OFFERED_CACHE_KEY
                : WORKSPACE_USER_CACHE_KEYS.ACCRUAL_MATCH_DRAWER_SCHEDULE_COLUMN_OFFERED_CACHE_KEY,
            z.boolean(),
        );
    // The cache reads storage once, so the ref carries "already offered"
    // through this session's re-reconciles.
    const scheduleColumnOfferedRef = useRef(wasScheduleColumnOffered === true);
    const offersLedgerColumns = columnsVariant === "workspaceMatched";
    const [wasCashColumnOffered, setCashColumnOffered] =
        useWorkspaceUserCache<boolean>(
            offersLedgerColumns
                ? WORKSPACE_USER_CACHE_KEYS.ACCRUAL_MATCH_DRAWER_WORKSPACE_CASH_COLUMN_OFFERED_CACHE_KEY
                : undefined,
            z.boolean(),
        );
    const [wasAccrualColumnOffered, setAccrualColumnOffered] =
        useWorkspaceUserCache<boolean>(
            offersLedgerColumns
                ? WORKSPACE_USER_CACHE_KEYS.ACCRUAL_MATCH_DRAWER_WORKSPACE_ACCRUAL_COLUMN_OFFERED_CACHE_KEY
                : undefined,
            z.boolean(),
        );
    const cashColumnOfferedRef = useRef(wasCashColumnOffered === true);
    const accrualColumnOfferedRef = useRef(wasAccrualColumnOffered === true);
    const reconcile = useCallback(
        (view: View | undefined) => {
            const merged = mergeCachedView(view, defaultView, viewConfig);
            const reconciled = offerScheduleColumnOnce({
                view: merged,
                isSchedulesEnabled,
                wasOffered: scheduleColumnOfferedRef.current,
            });
            if (isSchedulesEnabled) {
                scheduleColumnOfferedRef.current = true;
            }
            if (!offersLedgerColumns) {
                return reconciled;
            }
            const hasAccrualLedger = defaultView.columns.some(
                (column) => column.id === AccrualMatchColumn.ACCRUAL_LEDGER,
            );
            const next = enforceLedgerColumns({
                view: reconciled,
                hasAccrualLedger,
                wasCashOffered: cashColumnOfferedRef.current,
                wasAccrualOffered: accrualColumnOfferedRef.current,
            });
            cashColumnOfferedRef.current = true;
            if (hasAccrualLedger) {
                accrualColumnOfferedRef.current = true;
            }
            return next;
        },
        [defaultView, viewConfig, isSchedulesEnabled, offersLedgerColumns],
    );
    const [tableView, setTableView] = useState<View>(() =>
        reconcile(cachedView),
    );

    // `ledgerBasis`/`isGlFirst` fail open to optimistic defaults
    // (`useHasAccrualLedger`/`useIsGlFirst`) while their queries are still
    // loading, which can still be true when this provider first mounts (e.g.
    // a cold session). Without this, a first-time user (no cache yet) could
    // get a `defaultView` computed from a transient wrong flag value
    // permanently written to their cache below before the real value arrives.
    // Re-reconcile whenever the *settled* `defaultView` changes. We merge the
    // current in-session `tableView` (not the original `cachedView`) so column
    // edits the user made while the flags were loading survive; the settled
    // `viewConfig` drops columns that no longer apply, so a transiently-wrong
    // default still gets corrected without discarding those edits. `defaultView`
    // is memoized, so this only re-fires on a real flag change.
    const lastDefaultViewRef = useRef(defaultView);
    // `reconcile` is read through an effect event: this effect exists solely to
    // react to `defaultView` settling, not to re-run on every config identity
    // change (which would fight in-session column edits once everything has
    // already settled). `viewConfig` changes in lockstep with `defaultView`
    // (same flags), so the event always has the matching settled config.
    const reconcileTableView = useEffectEvent(() => {
        setTableView((prev) => reconcile(prev));
    });
    useEffect(() => {
        if (defaultView === lastDefaultViewRef.current) {
            return;
        }
        lastDefaultViewRef.current = defaultView;
        reconcileTableView();
    }, [defaultView]);

    useEffect(() => {
        setCachedView(tableView);
    }, [tableView, setCachedView]);

    useEffect(() => {
        if (isSchedulesEnabled && wasScheduleColumnOffered !== true) {
            setScheduleColumnOffered(true);
        }
    }, [
        isSchedulesEnabled,
        wasScheduleColumnOffered,
        setScheduleColumnOffered,
    ]);

    useEffect(() => {
        if (offersLedgerColumns && wasCashColumnOffered !== true) {
            setCashColumnOffered(true);
        }
    }, [offersLedgerColumns, wasCashColumnOffered, setCashColumnOffered]);

    const offersAccrualColumn = defaultView.columns.some(
        (column) => column.id === AccrualMatchColumn.ACCRUAL_LEDGER,
    );
    useEffect(() => {
        if (
            offersLedgerColumns &&
            offersAccrualColumn &&
            wasAccrualColumnOffered !== true
        ) {
            setAccrualColumnOffered(true);
        }
    }, [
        offersLedgerColumns,
        offersAccrualColumn,
        wasAccrualColumnOffered,
        setAccrualColumnOffered,
    ]);

    const setTableViewKeepingLedgerColumnsTogether = useCallback(
        (update: React.SetStateAction<View>) => {
            setTableView((prev) => {
                const next =
                    typeof update === "function" ? update(prev) : update;
                if (!offersLedgerColumns) {
                    return next;
                }
                return enforceLedgerColumns({
                    view: next,
                    hasAccrualLedger: offersAccrualColumn,
                    wasCashOffered: true,
                    wasAccrualOffered: true,
                });
            });
        },
        [offersAccrualColumn, offersLedgerColumns],
    );

    const viewValue = useMemo(
        () => ({
            tableView,
            setTableView: setTableViewKeepingLedgerColumnsTogether,
        }),
        [tableView, setTableViewKeepingLedgerColumnsTogether],
    );

    return (
        <AccrualMatchViewCtx.Provider value={viewValue}>
            {children}
        </AccrualMatchViewCtx.Provider>
    );
};
